import { useContext } from "react"
import moment from "moment"
import useName from "../../hooks/useName"
import { Link } from "react-router-dom"
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar"
import { Button } from "../ui/button"
import { Badge } from "../ui/badge"
import { getUserColor } from "../../utils/getUserColor"
import { Notification } from "../../models/app.interface"
import { NotificationCtx } from "../../context/notificationContext"
import { UserCtx } from "../../context/userContext"

type Props = {
  notification: Notification
}

export function NotificationCenterItem({ notification }: Props) {
  const { user } = useContext(UserCtx)
  const { markRead } = useContext(NotificationCtx)
  const { initial } = useName({ firstName: notification.creator_first_name })

  const timezone = user?.timezone?.value
  const notificationTime = moment
    ?.utc(notification.created_at)
    ?.tz(timezone)
    ?.format("lll")

  const getLink = () => {
    const activeProfile = user.activeProfile

    switch (notification.link) {
      case "/contacts":
        return `/${activeProfile}/contacts`
      case "/scheduling":
        return `/${activeProfile}/scheduling`
      case "/sessions":
        return `/${activeProfile}/sessions`
      case "/inbox":
        return `/${activeProfile}/inbox`
      default:
        return notification.link
    }
  }

  const handleNotificationClick = () => {
    if (notification.viewed === 0) {
      markRead(notification.id)
    }
  }

  return (
    <div className="border-border relative w-full border-b last:border-0">
      <Link
        to={getLink()}
        onClick={handleNotificationClick}
        className="ring-ring flex w-full items-center gap-4 rounded-md px-1 py-4 text-left focus-visible:ring-2"
      >
        <Avatar size="xs">
          {Boolean(notification.creator_has_avatar) && (
            <AvatarImage
              alt="avatar"
              src={`https://profile-avatars-${process.env.REACT_APP_USER_ENVIRONMENT}.s3.amazonaws.com/${notification.creator_user_id}`}
            />
          )}
          <AvatarFallback
            className={getUserColor(notification.creator_user_color)}
          >
            {initial}
          </AvatarFallback>
        </Avatar>

        <div className="flex flex-col gap-0.5">
          <h3 className="text-base">{notification.notification_name}</h3>
          <p className="text-muted-foreground text-xs">{notificationTime}</p>
        </div>
      </Link>

      {notification.viewed === 0 && (
        <Button
          onClick={(e) => {
            e.stopPropagation()
            markRead(notification.id)
          }}
          className="absolute bottom-[9px] right-[8px]"
          variant="ghost"
          size="sm"
        >
          <Badge dot variant="alert" />
          Mark Read
        </Button>
      )}
    </div>
  )
}
